/**
 * 时间胶囊在星海里的样子（ADR-0011，design §11.6）：一颗星茧，放在解封那天的位置，比照片星大，外面一圈封印的光环。
 * 阶段（capsule.ts 的 phaseOf）：
 * - 静默封印：暗色的茧，紫色的边慢慢呼吸，光环上的符印慢慢转；
 * - 最后 24 小时：表面浮出发光的裂纹，呼吸变成心跳一样的短促脉冲，颜色往金色走；
 * - 到了、你还没拆：充满金光、裂缝全开；长按时（aPress）越来越亮；
 * - 你拆开过：一颗亮星，外面一圈斜着的永久星环（像土星）。
 * 入场暗号的足迹（Ta 看过的那句，notes.ts）也画在这一层（阶段 4）：一颗小小的玫瑰色四芒星，比星茧小一半。
 * 一颗一个面片，一次画完；它们不多，点选在 CPU 上逐个投影。
 */
import * as THREE from 'three';

const VERT = /* glsl */ `
attribute vec3 aPos;
attribute float aPhase;
attribute float aSeed;
attribute float aPress;
uniform float uViewH;
uniform float uMinPx;
varying vec2 vUv;
varying float vPhase;
varying float vSeed;
varying float vPress;

void main() {
  vec4 mv = modelViewMatrix * vec4(aPos, 1.0);
  float dist = max(-mv.z, 0.001);
  float pxPerUnit = uViewH * 0.5 * projectionMatrix[1][1] / dist;
  // 星茧在世界里约 2.6 个单位宽、足迹 1.2；离得远时至少 uMinPx 像素（足迹六成），总览里也找得到
  bool foot = aPhase > 3.5;
  float size = (foot ? 1.2 : 2.6) * (1.0 + 0.25 * aPress);
  float minPx = uMinPx * (foot ? 0.6 : 1.0);
  if (size * pxPerUnit < minPx) size = minPx / pxPerUnit;
  mv.xy += position.xy * size;
  gl_Position = projectionMatrix * mv;
  vUv = position.xy * 2.0;
  vPhase = aPhase;
  vSeed = aSeed;
  vPress = aPress;
}
`;

const FRAG = /* glsl */ `
uniform float uTime;
uniform float uOpacity;
varying vec2 vUv;
varying float vPhase;
varying float vSeed;
varying float vPress;

float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p), u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}

void main() {
  vec2 p = vUv;
  float r = length(p);
  if (r >= 1.0) discard;
  float t = uTime + vSeed * 10.0;
  vec3 violet = vec3(0.55, 0.42, 0.95), gold = vec3(1.0, 0.8, 0.48);
  vec3 col;
  if (vPhase < 2.5) {
    // 呼吸：封印时 4 秒一次、很缓；预热以后像心跳，一强一弱
    float ph = fract(t / (vPhase < 0.5 ? 4.0 : 1.1));
    float beat = vPhase < 0.5 ? 0.5 + 0.5 * sin(ph * 6.2831853) : exp(-pow((ph - 0.08) / 0.07, 2.0)) + 0.6 * exp(-pow((ph - 0.3) / 0.08, 2.0));
    beat = mix(beat, 1.0, vPress);
    float warm = vPhase < 0.5 ? 0.0 : vPhase < 1.5 ? 0.45 : 1.0;
    vec3 tone = mix(violet, gold, warm);
    float shell = smoothstep(0.43, 0.37, r);
    float rim = exp(-pow((r - 0.4) / 0.03, 2.0));
    float halo = exp(-r * 4.0) * 0.55;
    // 封印光环和上面慢慢转的六个符印
    float ring = exp(-pow((r - 0.64) / 0.012, 2.0));
    float a = atan(p.y, p.x) + t * 0.15;
    float glyphs = ring * (0.3 + 0.7 * pow(0.5 + 0.5 * cos(a * 6.0), 10.0));
    col = vec3(0.06, 0.05, 0.12) * shell + tone * rim * (0.6 + 0.6 * beat) + tone * halo * (0.4 + 0.8 * beat) + mix(violet, gold, 0.6) * glyphs * (0.8 + 0.4 * beat);
    // 裂纹：预热以后才有；到了以后全开
    if (vPhase > 0.5) {
      float n = noise(p * 6.5 + vSeed * 13.0) + 0.5 * noise(p * 13.0 - vSeed * 7.0);
      float crack = smoothstep(0.05, 0.0, abs(n - 0.75)) * shell;
      col += gold * crack * (vPhase < 1.5 ? 0.9 * beat : 2.0 + 2.5 * vPress);
    }
    if (vPhase > 1.5) col += gold * exp(-r * 5.0) * (0.9 + 2.5 * vPress);
  } else if (vPhase < 3.5) {
    // 拆开过：一颗亮星和一圈斜着的星环
    float core = exp(-r * r * 40.0) * 1.6 + exp(-r * 6.0) * 0.45;
    float c = cos(0.45), s = sin(0.45);
    vec2 q = vec2(c * p.x + s * p.y, -s * p.x + c * p.y);
    float e = length(vec2(q.x / 0.8, q.y / 0.26));
    float band = exp(-pow((e - 1.0) / 0.07, 2.0));
    col = vec3(1.0, 0.93, 0.82) * core + gold * band * 0.85 * (0.85 + 0.15 * sin(t * 1.3));
  } else {
    // 足迹：一颗小小的玫瑰色四芒星，慢慢闪
    vec3 rose = vec3(1.0, 0.62, 0.74);
    float twinkle = 0.75 + 0.25 * sin(t * 1.7);
    float core = exp(-r * r * 30.0);
    float rays = exp(-abs(p.x) * 26.0) * exp(-abs(p.y) * 3.2) + exp(-abs(p.y) * 26.0) * exp(-abs(p.x) * 3.2);
    col = (vec3(1.0, 0.84, 0.88) * core * 1.1 + rose * rays * 0.8 + rose * exp(-r * 5.0) * 0.3) * twinkle;
  }
  gl_FragColor = vec4(col * uOpacity, 1.0);
  #include <colorspace_fragment>
}
`;

export interface CocoonInit {
  id: string;
  x: number;
  y: number;
  z: number;
  /** 0 静默封印、1 预热、2 等你拆、3 拆开过；4 是入场暗号的足迹 */
  phase: number;
  seed: number;
}

const QUAD = new Float32Array([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0]);

export class Cocoons {
  readonly mesh: THREE.Mesh<THREE.InstancedBufferGeometry, THREE.ShaderMaterial>;
  readonly uniforms = {
    uTime: { value: 0 },
    uViewH: { value: 800 },
    uMinPx: { value: 16 },
    uOpacity: { value: 1 },
  };
  private list: CocoonInit[] = [];
  private press: Float32Array = new Float32Array(0);
  private pressAttr: THREE.InstancedBufferAttribute | null = null;
  private readonly v = new THREE.Vector3();

  constructor() {
    const material = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: this.uniforms,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.mesh = new THREE.Mesh(this.build([]), material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 2;
  }

  private build(list: CocoonInit[]): THREE.InstancedBufferGeometry {
    const geo = new THREE.InstancedBufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(QUAD, 3));
    geo.setIndex([0, 1, 2, 0, 2, 3]);
    const n = Math.max(1, list.length);
    const pos = new Float32Array(n * 3), phase = new Float32Array(n), seed = new Float32Array(n);
    list.forEach((c, k) => {
      pos.set([c.x, c.y, c.z], k * 3);
      phase[k] = c.phase;
      seed[k] = c.seed;
    });
    this.press = new Float32Array(n);
    this.pressAttr = new THREE.InstancedBufferAttribute(this.press, 1);
    this.pressAttr.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aPos', new THREE.InstancedBufferAttribute(pos, 3));
    geo.setAttribute('aPhase', new THREE.InstancedBufferAttribute(phase, 1));
    geo.setAttribute('aSeed', new THREE.InstancedBufferAttribute(seed, 1));
    geo.setAttribute('aPress', this.pressAttr);
    geo.instanceCount = list.length;
    return geo;
  }

  /** 整个换掉（胶囊不多，每次重建） */
  set(list: CocoonInit[]): void {
    this.list = list;
    const old = this.mesh.geometry;
    this.mesh.geometry = this.build(list);
    old.dispose();
  }

  /** 长按的进度 0–1：越按越亮 */
  setPress(id: string, k: number): void {
    const i = this.list.findIndex((c) => c.id === id);
    if (i < 0 || !this.pressAttr) return;
    this.press[i] = k;
    this.pressAttr.needsUpdate = true;
  }

  position(id: string, out = new THREE.Vector3()): THREE.Vector3 | null {
    const c = this.list.find((x) => x.id === id);
    return c ? out.set(c.x, c.y, c.z) : null;
  }

  /** 屏幕上 (x, y)（相对画布的 CSS 像素）点中的那颗：离中心不到它半径（星茧至少 18 像素、足迹至少 14 像素）的里面最近的 */
  pick(camera: THREE.Camera, x: number, y: number, width: number, height: number): string | null {
    let best: string | null = null, bestD = Number.POSITIVE_INFINITY;
    const proj = camera as THREE.PerspectiveCamera;
    for (const c of this.list) {
      const v = this.v.set(c.x, c.y, c.z).applyMatrix4(proj.matrixWorldInverse);
      if (v.z >= 0) continue;
      const pxPerUnit = (height * 0.5 * proj.projectionMatrix.elements[5]!) / -v.z;
      v.applyMatrix4(proj.projectionMatrix);
      const sx = ((v.x + 1) / 2) * width, sy = ((1 - v.y) / 2) * height;
      const d = Math.hypot(sx - x, sy - y), radius = c.phase > 3.5 ? Math.max(14, 1.2 * pxPerUnit * 0.5) : Math.max(18, 2.6 * pxPerUnit * 0.4);
      if (d <= radius && d < bestD) {
        best = c.id;
        bestD = d;
      }
    }
    return best;
  }
}
